import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button/Button';
import TextArea from '../../../components/TextArea/TextArea';
import styles from './CancelAppointmentModal.module.css';

interface CancelAppointmentModalProps {
    isOpen: boolean;
    appointmentId: number;
    customerName: string;
    onClose: () => void;
    onConfirmCancel: (id: number, reason?: string) => Promise<void>;
}

const CancelAppointmentModal: React.FC<CancelAppointmentModalProps> = ({
    isOpen,
    appointmentId,
    customerName,
    onClose,
    onConfirmCancel
}) => {
    const [reason, setReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setReason('');
            setIsLoading(false);
        }
    }, [isOpen]);

    const handleConfirmCancel = async () => {
        setIsLoading(true);
        try {
            await onConfirmCancel(appointmentId, reason.trim() || undefined);
        } catch (error) {
            // Error handling is done by parent
        } finally {
            setIsLoading(false);
        }
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !isLoading) {
            onClose();
        }
    };

    const handleReasonChange = (value: string) => {
        setReason(value);
    };

    if (!isOpen) return null;

    return (
        <div
            className={`${styles.modalOverlay} ${isOpen ? styles.open : ''}`}
            onClick={handleOverlayClick}
        >
            <div className={`${styles.modalContent} ${isOpen ? styles.open : ''}`}>
                {isLoading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loadingContent}>
                            <div className={styles.spinner}></div>
                            <span>Cancelling appointment...</span>
                        </div>
                    </div>
                )}

                <button
                    className={styles.closeButton}
                    onClick={onClose}
                    disabled={isLoading}
                    aria-label="Close modal"
                >
                    &times;
                </button>

                <div className={styles.headerContainer}>
                    <h2 className={styles.header}>Cancel Appointment</h2>
                    <p className={styles.warningText}>
                        Are you sure you want to cancel the appointment with <strong>{customerName}</strong>?
                        This action will free up the slot for other bookings.
                    </p>
                </div>

                <div className={styles.reasonSection}>
                    <label className={styles.reasonLabel}>
                        Reason for cancellation <span className={styles.optionalTag}>(optional)</span>
                    </label>
                    <TextArea
                        value={reason}
                        onChange={handleReasonChange}
                        placeholder="Enter reason for cancellation..."
                        disabled={isLoading}
                    />
                </div>

                <div className={styles.buttonsContainer}>
                    <Button
                        label="Keep Appointment"
                        onClick={onClose}
                        noAppearance={true}
                        size="small"
                        disabled={isLoading}
                    />
                    <Button
                        label={isLoading ? 'Cancelling...' : 'Cancel Appointment'}
                        onClick={handleConfirmCancel}
                        size="small"
                        backgroundColor="var(--destructive-fill)"
                        fontColor="var(--on-destructive)"
                        disabled={isLoading}
                    />
                </div>
            </div>
        </div>
    );
};

export default CancelAppointmentModal;
